/**
 * T03 — 폐수배출시설 및 수질오염방지시설 운영일지
 * 「물환경보전법 시행규칙」 [별지 제18호서식] <개정 2021. 9. 16.>
 * ---------------------------------------------------------------------------
 * 세 서식 중 가장 크다. 표가 11개, 칸이 수백 개다.
 *
 * 한 화면에 전부 펼치면 스크롤이 길어 사람이 길을 잃는다. 접이식 구역으로
 * 나누되 **기본은 모두 펼침**이다 — 접혀 있으면 적어야 할 칸을 빠뜨린다.
 *
 * 구역 번호가 인쇄물과 다른 이유
 *   종이 서식은 좁은 두 장에 몰아 넣느라 성격이 다른 값을 한 표에 붙여 놓았다
 *   — 근무자 이름이 가동표 안에 들어가 있고, 용수 사용량과 폐수 배출량이
 *   좌우로 붙어 한 번호를 쓴다. 화면은 폭이 넉넉하니 이것들을 각각 제 구역으로
 *   떼어 14개로 나눈다. 인쇄는 법정 서식이므로 1~12번 그대로 나간다.
 *
 * 1~6번(인쇄물 1~4번)은 반드시 적어야 하고 나머지는 사업자 판단에 따라 선택이다.
 */

import type { ReactNode } from 'react';
import { Alert, Button, Card, Collapse, Descriptions, Input, Table, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import type {
  AnalysisRow,
  AutoCheckRow,
  ChemicalRow,
  FacilityHoursRow,
  MaterialRow,
  PreventionHoursRow,
  SludgeRow,
  WaterSelfBody,
  WaterUsageRow,
} from '@eco/shared';
import { EMPTY_HOURS, WATER_DISCHARGE_CATEGORIES, WATER_SUPPLY_CATEGORIES } from '@eco/shared';
import {
  DateCell,
  HourGrid,
  NumberCell,
  TextCell,
  TimeCell,
  appendRow,
  patchRow,
  removeRow,
} from './rows';

const { Text } = Typography;

export interface WaterSelfSections {
  body: WaterSelfBody;
  facilityHours: FacilityHoursRow[];
  preventionHours: PreventionHoursRow[];
  waterUsages: WaterUsageRow[];
  sludges: SludgeRow[];
  materials: MaterialRow[];
  chemicals: ChemicalRow[];
  analyses: AnalysisRow[];
  autoChecks: AutoCheckRow[];
}

export interface WaterSelfHandlers {
  onBody: (body: WaterSelfBody) => void;
  onFacilityHours: (rows: FacilityHoursRow[]) => void;
  onPreventionHours: (rows: PreventionHoursRow[]) => void;
  onWaterUsages: (rows: WaterUsageRow[]) => void;
  onSludges: (rows: SludgeRow[]) => void;
  onMaterials: (rows: MaterialRow[]) => void;
  onChemicals: (rows: ChemicalRow[]) => void;
  onAnalyses: (rows: AnalysisRow[]) => void;
  onAutoChecks: (rows: AutoCheckRow[]) => void;
}

const SUPPLY_SET: ReadonlySet<string> = new Set(WATER_SUPPLY_CATEGORIES);
const DISCHARGE_SET: ReadonlySet<string> = new Set(WATER_DISCHARGE_CATEGORIES);

const EMPTY_USAGE: WaterUsageRow = {
  category: '',
  prevGauge: null,
  currGauge: null,
  usageAmount: null,
  checkTime: null,
  note: null,
  sortOrder: 0,
};

const EMPTY_SLUDGE: SludgeRow = {
  generationAmount: null,
  treatmentAmount: null,
  storageAmount: null,
  waterContent: null,
  storageLocation: null,
  selfTreatmentLocation: null,
  outsourcedCompany: null,
  note: null,
  sortOrder: 0,
};

const EMPTY_MATERIAL: MaterialRow = {
  materialName: '',
  quantity: null,
  // 서식의 칸 이름이 「사용량(㎏)」 이다. 단위를 사람에게 묻지 않는다.
  unit: 'kg',
  note: null,
  sortOrder: 0,
};

const EMPTY_CHEMICAL: ChemicalRow = {
  chemicalName: '',
  purchaseAmount: null,
  usageAmount: null,
  remainingAmount: null,
  // 약품 수량도 모두 ㎏ 이다.
  unit: 'kg',
  remarks: null,
  sortOrder: 0,
};

const EMPTY_ANALYSIS: AnalysisRow = {
  category: '',
  phValue: null,
  bodValue: null,
  tocValue: null,
  codValue: null,
  ssValue: null,
  nHexValue: null,
  cnValue: null,
  cuValue: null,
  userDef1: null,
  userDef2: null,
  userDef3: null,
  userDef4: null,
  valueNote: null,
  analyzedAt: null,
  sortOrder: 0,
};

const EMPTY_AUTO_CHECK: AutoCheckRow = {
  checkItem: '',
  phStatus: null,
  bodStatus: null,
  tocStatus: null,
  codStatus: null,
  ssStatus: null,
  tnStatus: null,
  tpStatus: null,
  flowStatus: null,
  samplerStatus: null,
  note: null,
  sortOrder: 0,
};

/**
 * 용수 표의 한 줄.
 *
 * 서식이 이름을 못 박아 둔 줄은 **일지에 그 줄이 없어도** 화면에 보여야 한다.
 * 예전에 만든 일지에는 열여섯 줄이 다 들어 있지 않아, 있는 줄만 그리면 폐수
 * 배출량 표가 통째로 비어 적을 자리가 없었다. index 가 -1 이면 아직 없는 줄이고
 * 값을 처음 적을 때 만든다.
 */
interface UsageSlot {
  category: string;
  index: number;
  /** 서식이 못 박은 줄인가. 이름을 고치거나 지울 수 없다. */
  fixed: boolean;
}

/** 서식 아래에 달린 ※ 주. 화면에도 같은 자리에 둔다. */
function FormNote({ children }: { children: ReactNode }) {
  return (
    <div style={{ marginTop: 8 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        ※ {children}
      </Text>
    </div>
  );
}

export function WaterSelfForm({
  sections,
  locked,
  handlers,
}: {
  sections: WaterSelfSections;
  locked: boolean;
  handlers: WaterSelfHandlers;
}) {
  const navigate = useNavigate();
  const {
    body,
    facilityHours,
    preventionHours,
    waterUsages,
    sludges,
    materials,
    chemicals,
    analyses,
    autoChecks,
  } = sections;

  const set = <K extends keyof WaterSelfBody>(key: K, value: WaterSelfBody[K]) =>
    handlers.onBody({ ...body, [key]: value });

  /**
   * 구역 머리에 놓는 「행 추가」.
   *
   * 머리 줄 자체가 접기 단추라, 그 위에 얹은 단추의 클릭이 위로 전해지면 행을
   * 더하면서 구역이 접힌다. 방금 더한 줄이 눈앞에서 사라지므로 전파를 끊는다.
   */
  const addButton = (label: string, onClick: () => void) =>
    !locked && (
      <Button
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      >
        {label}
      </Button>
    );

  /** 삭제 열. 여러 표가 같은 모양을 쓴다. */
  const deleteColumn = <T,>(rows: T[], onChange: (next: T[]) => void) => ({
    title: '',
    width: 60,
    render: (_: unknown, __: T, i: number) =>
      !locked && (
        <Button size="small" type="link" danger onClick={() => onChange(removeRow(rows, i))}>
          삭제
        </Button>
      ),
  });

  /* ---- 용수 / 폐수 배출량 ------------------------------------------------ */

  const slotFor = (category: string): UsageSlot => ({
    category,
    index: waterUsages.findIndex((r) => r.category === category),
    fixed: true,
  });

  const supplySlots: UsageSlot[] = [
    ...WATER_SUPPLY_CATEGORIES.map(slotFor),
    // 서식에 없는 공급원을 사업장이 더한 줄.
    ...waterUsages
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => !SUPPLY_SET.has(row.category) && !DISCHARGE_SET.has(row.category))
      .map(({ row, index }) => ({ category: row.category, index, fixed: false })),
  ];
  const dischargeSlots: UsageSlot[] = WATER_DISCHARGE_CATEGORIES.map(slotFor);

  const usageAt = (slot: UsageSlot): WaterUsageRow | null =>
    slot.index >= 0 ? (waterUsages[slot.index] ?? null) : null;

  const patchUsage = (slot: UsageSlot, patch: Partial<WaterUsageRow>) => {
    if (slot.index >= 0) {
      handlers.onWaterUsages(patchRow(waterUsages, slot.index, patch));
      return;
    }
    // 아직 없는 줄이다. 값을 적는 순간 만든다.
    handlers.onWaterUsages(
      appendRow(waterUsages, { ...EMPTY_USAGE, ...patch, category: slot.category }),
    );
  };

  const usageNumberColumn = (
    field: 'prevGauge' | 'currGauge' | 'usageAmount',
    title: string,
    width: number,
    placeholder?: string,
  ) => ({
    title,
    width,
    render: (_: unknown, slot: UsageSlot) => (
      <NumberCell
        value={usageAt(slot)?.[field] ?? null}
        disabled={locked}
        min={0}
        placeholder={placeholder}
        onChange={(v) => patchUsage(slot, { [field]: v })}
      />
    ),
  });

  const usageCategoryColumn = {
    title: '구분',
    width: 160,
    render: (_: unknown, slot: UsageSlot) =>
      slot.fixed ? (
        <Text>{slot.category}</Text>
      ) : (
        <TextCell
          value={slot.category}
          disabled={locked}
          maxLength={60}
          onChange={(v) => patchUsage(slot, { category: v ?? '' })}
        />
      ),
  };

  const items = [
    {
      key: 'facility-hours',
      label: '1. 폐수배출시설 가동(조업)시간대',
      extra: <Tag color="red">필수</Tag>,
      children: (
        <Table<FacilityHoursRow>
          className="eco-grid"
          rowKey={(_, i) => String(i)}
          size="small"
          pagination={false}
          dataSource={facilityHours}
          locale={{ emptyText: '등록된 폐수배출시설이 없습니다.' }}
          columns={[
            { title: '구분', dataIndex: 'facilityName', width: 160 },
            {
              title: '시간대 (드래그로 연속 선택)',
              render: (_, row, i) => (
                <HourGrid
                  hours={row.hours ?? EMPTY_HOURS}
                  disabled={locked}
                  onChange={(hours) =>
                    handlers.onFacilityHours(patchRow(facilityHours, i, { hours }))
                  }
                />
              ),
            },
          ]}
        />
      ),
    },
    {
      key: 'prevention-hours',
      label: '2. 수질오염방지시설 가동시간대',
      extra: <Tag color="red">필수</Tag>,
      children: (
        <>
          <Table<PreventionHoursRow>
            className="eco-grid"
            rowKey={(_, i) => String(i)}
            size="small"
            pagination={false}
            dataSource={preventionHours}
            locale={{ emptyText: '등록된 수질오염방지시설이 없습니다.' }}
            columns={[
              { title: '구분', dataIndex: 'facilityName', width: 160 },
              {
                title: '시간대 (드래그로 연속 선택)',
                render: (_, row, i) => (
                  <HourGrid
                    hours={row.hours ?? EMPTY_HOURS}
                    disabled={locked}
                    onChange={(hours) =>
                      handlers.onPreventionHours(patchRow(preventionHours, i, { hours }))
                    }
                  />
                ),
              },
            ]}
          />
          <FormNote>시간대 표시는 검은색으로 인쇄됩니다.</FormNote>
        </>
      ),
    },
    {
      key: 'shift-workers',
      label: '3. 시간대별 근무자 직함 및 성명',
      extra: <Tag color="red">필수</Tag>,
      children: (
        <>
          <Input.TextArea
            value={body.shiftWorkers ?? ''}
            disabled={locked}
            maxLength={1000}
            autoSize={{ minRows: 3, maxRows: 8 }}
            placeholder={'반장 홍길동 (08:00~18:00)\n주임 김철수 (18:00~08:00)'}
            onChange={(e) => set('shiftWorkers', e.target.value === '' ? null : e.target.value)}
          />
          <FormNote>
            그 날 근무한 사람을 한 명씩 줄을 바꿔 적습니다. 인쇄할 때는 서식의 「2. 수질오염방지시설
            가동시간대」 표 아래 한 줄로 이어 붙습니다.
          </FormNote>
        </>
      ),
    },
    {
      key: 'water-supply',
      label: '4. 용수 공급원별 사용량',
      extra: (
        <>
          <Tag color="red">필수</Tag>
          {addButton('공급원 추가', () =>
            handlers.onWaterUsages(appendRow(waterUsages, EMPTY_USAGE)),
          )}
        </>
      ),
      children: (
        <>
          <Table<UsageSlot>
            className="eco-grid"
            rowKey={(slot) => (slot.fixed ? slot.category : `x${slot.index}`)}
            size="small"
            pagination={false}
            dataSource={supplySlots}
            columns={[
              usageCategoryColumn,
              usageNumberColumn('prevGauge', '전일지침(㎥)', 150),
              usageNumberColumn('currGauge', '금일지침(㎥)', 150),
              // 비워 두면 서버가 지침 차이로 계산한다. 매일 빼는 것은 사람이 할 일이 아니다.
              usageNumberColumn('usageAmount', '사용량(㎥/일)', 150, '자동'),
              {
                title: '검침시간대',
                width: 130,
                render: (_, slot) => (
                  <TimeCell
                    value={usageAt(slot)?.checkTime ?? null}
                    disabled={locked}
                    onChange={(v) => patchUsage(slot, { checkTime: v })}
                  />
                ),
              },
              {
                title: '',
                width: 60,
                render: (_, slot) =>
                  !locked &&
                  !slot.fixed && (
                    <Button
                      size="small"
                      type="link"
                      danger
                      onClick={() => handlers.onWaterUsages(removeRow(waterUsages, slot.index))}
                    >
                      삭제
                    </Button>
                  ),
              },
            ]}
          />
          <FormNote>
            서식이 정한 공급원은 지울 수 없습니다. 「계」는 사용량을 더해 인쇄되고, 서식에 없는
            공급원은 「공급원 추가」로 넣습니다.
          </FormNote>
        </>
      ),
    },
    {
      key: 'water-discharge',
      label: '5. 폐수 배출량',
      extra: <Tag color="red">필수</Tag>,
      children: (
        <>
          <Table<UsageSlot>
            className="eco-grid"
            rowKey={(slot) => slot.category}
            size="small"
            pagination={false}
            dataSource={dischargeSlots}
            columns={[
              usageCategoryColumn,
              usageNumberColumn('prevGauge', '전일지침(㎥)', 150),
              usageNumberColumn('currGauge', '금일지침(㎥)', 150),
              usageNumberColumn('usageAmount', '배출량 및 사용량(㎥/일)', 200, '자동'),
            ]}
          />
          <FormNote>
            인쇄할 때는 서식대로 「4. 용수 공급원별 사용량」 오른쪽에 나란히 붙습니다.
          </FormNote>
        </>
      ),
    },
    {
      key: 'sludge',
      label: '6. 슬러지의 발생량 및 처리량',
      extra: (
        <>
          <Tag color="red">필수</Tag>
          {addButton('행 추가', () => handlers.onSludges(appendRow(sludges, EMPTY_SLUDGE)))}
        </>
      ),
      children: (
        <>
          <Table<SludgeRow>
            className="eco-grid"
            rowKey={(_, i) => String(i)}
            size="small"
            pagination={false}
            dataSource={sludges}
            locale={{ emptyText: '슬러지 발생이 없으면 비워 둡니다.' }}
            columns={[
              {
                title: '슬러지발생량(㎥)',
                width: 160,
                render: (_, row, i) => (
                  <NumberCell
                    value={row.generationAmount}
                    disabled={locked}
                    min={0}
                    onChange={(v) =>
                      handlers.onSludges(patchRow(sludges, i, { generationAmount: v }))
                    }
                  />
                ),
              },
              {
                title: '처리량(㎥)',
                width: 150,
                render: (_, row, i) => (
                  <NumberCell
                    value={row.treatmentAmount}
                    disabled={locked}
                    min={0}
                    onChange={(v) =>
                      handlers.onSludges(patchRow(sludges, i, { treatmentAmount: v }))
                    }
                  />
                ),
              },
              {
                title: '보관량(㎥)',
                width: 150,
                render: (_, row, i) => (
                  <NumberCell
                    value={row.storageAmount}
                    disabled={locked}
                    min={0}
                    onChange={(v) => handlers.onSludges(patchRow(sludges, i, { storageAmount: v }))}
                  />
                ),
              },
              {
                title: '함수율(%)',
                width: 130,
                render: (_, row, i) => (
                  <NumberCell
                    value={row.waterContent}
                    disabled={locked}
                    min={0}
                    max={100}
                    onChange={(v) => handlers.onSludges(patchRow(sludges, i, { waterContent: v }))}
                  />
                ),
              },
              {
                title: '보관장소',
                render: (_, row, i) => (
                  <TextCell
                    value={row.storageLocation}
                    disabled={locked}
                    onChange={(v) =>
                      handlers.onSludges(patchRow(sludges, i, { storageLocation: v }))
                    }
                  />
                ),
              },
              deleteColumn(sludges, handlers.onSludges),
            ]}
          />
          <FormNote>
            함수율(%)란은 슬러지를 처리하는 경우 또는 슬러지를 적정하게 관리·처리할 수 있는
            주기(월 1회 등)마다 작성합니다.
          </FormNote>
        </>
      ),
    },
    {
      key: 'material',
      label: '7. 원료 또는 첨가제 등의 사용량',
      extra: addButton('행 추가', () => handlers.onMaterials(appendRow(materials, EMPTY_MATERIAL))),
      children: (
        <>
          <Table<MaterialRow>
            className="eco-grid"
            rowKey={(_, i) => String(i)}
            size="small"
            pagination={false}
            dataSource={materials}
            locale={{ emptyText: '사용한 원료가 없으면 비워 둡니다.' }}
            columns={[
              {
                title: '원료 또는 첨가제 등',
                render: (_, row, i) => (
                  <TextCell
                    value={row.materialName}
                    disabled={locked}
                    onChange={(v) =>
                      handlers.onMaterials(patchRow(materials, i, { materialName: v ?? '' }))
                    }
                  />
                ),
              },
              {
                title: '사용량(㎏)',
                width: 180,
                render: (_, row, i) => (
                  <NumberCell
                    value={row.quantity}
                    disabled={locked}
                    min={0}
                    onChange={(v) => handlers.onMaterials(patchRow(materials, i, { quantity: v }))}
                  />
                ),
              },
              deleteColumn(materials, handlers.onMaterials),
            ]}
          />
          <FormNote>일반적으로 사용되는 용어 또는 공통어로 기재합니다.</FormNote>
        </>
      ),
    },
    {
      key: 'power',
      label: '8. 전력사용량',
      children: (
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, xl: 3 }}>
          <Descriptions.Item label="가동시간">
            <TextCell
              value={body.powerOperatingTime}
              disabled={locked}
              maxLength={30}
              placeholder="08:00 ~ 18:00"
              onChange={(v) => set('powerOperatingTime', v)}
            />
          </Descriptions.Item>
          <Descriptions.Item label="사용량(kWh)">
            <NumberCell
              value={body.powerUsageAmount}
              disabled={locked}
              min={0}
              onChange={(v) => set('powerUsageAmount', v)}
            />
          </Descriptions.Item>
          <Descriptions.Item label="금일 폐수 1㎥당 소모전력량(kWh/㎥)">
            <NumberCell
              value={body.powerPerWater}
              disabled={locked}
              min={0}
              onChange={(v) => set('powerPerWater', v)}
            />
          </Descriptions.Item>
          <Descriptions.Item label="검침시간">
            <TimeCell
              value={body.powerCheckTime}
              disabled={locked}
              onChange={(v) => set('powerCheckTime', v)}
            />
          </Descriptions.Item>
          <Descriptions.Item label="적산전력계 지침">
            <NumberCell
              value={body.powerTotalGauge}
              disabled={locked}
              min={0}
              onChange={(v) => set('powerTotalGauge', v)}
            />
          </Descriptions.Item>
          <Descriptions.Item label="참고사항">
            <TextCell
              value={body.powerRemarks}
              disabled={locked}
              maxLength={500}
              onChange={(v) => set('powerRemarks', v)}
            />
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'chemical',
      label: '9. 약품사용량',
      extra: addButton('행 추가', () => handlers.onChemicals(appendRow(chemicals, EMPTY_CHEMICAL))),
      children: (
        <Table<ChemicalRow>
          className="eco-grid"
          rowKey={(_, i) => String(i)}
          size="small"
          pagination={false}
          dataSource={chemicals}
          locale={{ emptyText: '사용한 약품이 없으면 비워 둡니다.' }}
          columns={[
            {
              title: '약품명',
              render: (_, row, i) => (
                <TextCell
                  value={row.chemicalName}
                  disabled={locked}
                  onChange={(v) =>
                    handlers.onChemicals(patchRow(chemicals, i, { chemicalName: v ?? '' }))
                  }
                />
              ),
            },
            {
              title: '구입량(㎏)',
              width: 150,
              render: (_, row, i) => (
                <NumberCell
                  value={row.purchaseAmount}
                  disabled={locked}
                  min={0}
                  onChange={(v) =>
                    handlers.onChemicals(patchRow(chemicals, i, { purchaseAmount: v }))
                  }
                />
              ),
            },
            {
              title: '약품 소모량(㎏)',
              width: 160,
              render: (_, row, i) => (
                <NumberCell
                  value={row.usageAmount}
                  disabled={locked}
                  min={0}
                  onChange={(v) => handlers.onChemicals(patchRow(chemicals, i, { usageAmount: v }))}
                />
              ),
            },
            {
              title: '잔량(㎏)',
              width: 150,
              render: (_, row, i) => (
                <NumberCell
                  value={row.remainingAmount}
                  disabled={locked}
                  min={0}
                  onChange={(v) =>
                    handlers.onChemicals(patchRow(chemicals, i, { remainingAmount: v }))
                  }
                />
              ),
            },
            {
              title: '비고',
              render: (_, row, i) => (
                <TextCell
                  value={row.remarks}
                  disabled={locked}
                  maxLength={300}
                  onChange={(v) => handlers.onChemicals(patchRow(chemicals, i, { remarks: v }))}
                />
              ),
            },
            deleteColumn(chemicals, handlers.onChemicals),
          ]}
        />
      ),
    },
    {
      key: 'aeration',
      label: '10. 폭기조 운전상태(생물화학적 처리시설의 경우)',
      children: (
        <>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, xl: 4 }}>
            <Descriptions.Item label="pH">
              <NumberCell
                value={body.aerationPh}
                disabled={locked}
                min={0}
                max={14}
                step={0.1}
                onChange={(v) => set('aerationPh', v)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="수온">
              <NumberCell
                value={body.aerationWaterTemp}
                disabled={locked}
                step={0.1}
                onChange={(v) => set('aerationWaterTemp', v)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="DO">
              <NumberCell
                value={body.aerationDo}
                disabled={locked}
                min={0}
                step={0.1}
                onChange={(v) => set('aerationDo', v)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="SV30">
              <NumberCell
                value={body.aerationSv30}
                disabled={locked}
                min={0}
                onChange={(v) => set('aerationSv30', v)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="MLSS">
              <NumberCell
                value={body.aerationMlss}
                disabled={locked}
                min={0}
                onChange={(v) => set('aerationMlss', v)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="SVI">
              <NumberCell
                value={body.aerationSvi}
                disabled={locked}
                min={0}
                onChange={(v) => set('aerationSvi', v)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="폭기시간">
              <TextCell
                value={body.aerationTime}
                disabled={locked}
                maxLength={30}
                onChange={(v) => set('aerationTime', v)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="주미생물상태">
              <TextCell
                value={body.aerationMicrobeStatus}
                disabled={locked}
                maxLength={300}
                placeholder="양호 / 불량"
                onChange={(v) => set('aerationMicrobeStatus', v)}
              />
            </Descriptions.Item>
          </Descriptions>
          <FormNote>
            미생물 관찰: 현미경 보유(600배율 이상). 주미생물상태는 양호 또는 불량으로 적습니다.
          </FormNote>
        </>
      ),
    },
    {
      key: 'breakdown',
      label: '11. 수질오염방지시설 고장 유무 및 특기사항',
      children: (
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="고장 유무">
            <TextCell
              value={body.breakdownStatus}
              disabled={locked}
              maxLength={300}
              placeholder="이상 없음"
              onChange={(v) => set('breakdownStatus', v)}
            />
          </Descriptions.Item>
          <Descriptions.Item label="특기사항">
            <TextCell
              value={body.specialNotes}
              disabled={locked}
              maxLength={4000}
              onChange={(v) => set('specialNotes', v)}
            />
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'analysis',
      label: '12. 수질오염물질 측정내용',
      extra: addButton('행 추가', () => handlers.onAnalyses(appendRow(analyses, EMPTY_ANALYSIS))),
      children: (
        <>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, xl: 3 }}>
            <Descriptions.Item label="분석자명">
              <TextCell
                value={body.analyzerName}
                disabled={locked}
                maxLength={100}
                placeholder="사업장에서 분석하는 경우"
                onChange={(v) => set('analyzerName', v)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="측정대행업소명" span={2}>
              <TextCell
                value={body.outsourcedAnalysisCompany}
                disabled={locked}
                placeholder="분석을 위탁하는 경우"
                onChange={(v) => set('outsourcedAnalysisCompany', v)}
              />
            </Descriptions.Item>
            {([1, 2, 3, 4] as const).map((n) => (
              <Descriptions.Item key={n} label={`추가 항목 ${n}`}>
                <TextCell
                  value={body[`userDefTitle${n}` as const]}
                  disabled={locked}
                  maxLength={60}
                  placeholder="항목명 (예: T-N)"
                  onChange={(v) => set(`userDefTitle${n}` as const, v)}
                />
              </Descriptions.Item>
            ))}
          </Descriptions>

          <Table<AnalysisRow>
            className="eco-grid"
            rowKey={(_, i) => String(i)}
            size="small"
            pagination={false}
            scroll={{ x: 1400 }}
            style={{ marginTop: 12 }}
            dataSource={analyses}
            columns={[
              {
                title: '구분',
                width: 120,
                fixed: 'left',
                render: (_, row, i) => (
                  <TextCell
                    value={row.category}
                    disabled={locked}
                    maxLength={60}
                    onChange={(v) =>
                      handlers.onAnalyses(patchRow(analyses, i, { category: v ?? '' }))
                    }
                  />
                ),
              },
              ...(
                [
                  ['phValue', 'pH'],
                  ['bodValue', 'BOD'],
                  ['tocValue', 'TOC'],
                  ['codValue', 'COD'],
                  ['ssValue', 'SS'],
                  ['nHexValue', 'n-Hex'],
                  ['cnValue', '시안(CN)'],
                  ['cuValue', 'Cu'],
                ] as const
              ).map(([field, label]) => ({
                title: label,
                width: 90,
                render: (_: unknown, row: AnalysisRow, i: number) => (
                  <NumberCell
                    value={row[field]}
                    disabled={locked}
                    min={0}
                    max={field === 'phValue' ? 14 : undefined}
                    onChange={(v) => handlers.onAnalyses(patchRow(analyses, i, { [field]: v }))}
                  />
                ),
              })),
              ...(([1, 2, 3, 4] as const)
                .filter((n) => !!body[`userDefTitle${n}` as const])
                .map((n) => ({
                  title: body[`userDefTitle${n}` as const] ?? `추가 ${n}`,
                  width: 90,
                  render: (_: unknown, row: AnalysisRow, i: number) => (
                    <NumberCell
                      value={row[`userDef${n}` as const]}
                      disabled={locked}
                      onChange={(v) =>
                        handlers.onAnalyses(patchRow(analyses, i, { [`userDef${n}`]: v }))
                      }
                    />
                  ),
                }))),
              {
                title: '분석일',
                width: 150,
                render: (_, row, i) => (
                  <DateCell
                    value={row.analyzedAt}
                    disabled={locked}
                    onChange={(v) => handlers.onAnalyses(patchRow(analyses, i, { analyzedAt: v }))}
                  />
                ),
              },
              {
                title: '표기',
                width: 130,
                render: (_, row, i) => (
                  <TextCell
                    value={row.valueNote}
                    disabled={locked}
                    maxLength={300}
                    // "불검출" 처럼 수치로 담을 수 없는 값을 적는 칸이다.
                    placeholder="불검출 등"
                    onChange={(v) => handlers.onAnalyses(patchRow(analyses, i, { valueNote: v }))}
                  />
                ),
              },
              deleteColumn(analyses, handlers.onAnalyses),
            ]}
          />
        </>
      ),
    },
    {
      key: 'auto',
      label: '13. 수질자동측정기기 등의 측정항목별 점검내용',
      extra: addButton('행 추가', () =>
        handlers.onAutoChecks(appendRow(autoChecks, EMPTY_AUTO_CHECK)),
      ),
      children: (
        <Table<AutoCheckRow>
          className="eco-grid"
          rowKey={(_, i) => String(i)}
          size="small"
          pagination={false}
          scroll={{ x: 1200 }}
          dataSource={autoChecks}
          locale={{ emptyText: '자동측정기기가 없으면 비워 둡니다.' }}
          columns={[
            {
              title: '구분',
              width: 200,
              fixed: 'left',
              render: (_, row, i) => (
                <TextCell
                  value={row.checkItem}
                  disabled={locked}
                  maxLength={100}
                  onChange={(v) =>
                    handlers.onAutoChecks(patchRow(autoChecks, i, { checkItem: v ?? '' }))
                  }
                />
              ),
            },
            ...(
              [
                ['phStatus', 'pH'],
                ['bodStatus', 'BOD'],
                ['tocStatus', 'TOC'],
                ['codStatus', 'COD'],
                ['ssStatus', 'SS'],
                ['tnStatus', 'T-N'],
                ['tpStatus', 'T-P'],
                ['flowStatus', '유량계'],
                ['samplerStatus', '시료채취조'],
              ] as const
            ).map(([field, label]) => ({
              title: label,
              width: 100,
              render: (_: unknown, row: AutoCheckRow, i: number) => (
                <TextCell
                  value={row[field]}
                  disabled={locked}
                  maxLength={40}
                  onChange={(v) => handlers.onAutoChecks(patchRow(autoChecks, i, { [field]: v }))}
                />
              ),
            })),
            deleteColumn(autoChecks, handlers.onAutoChecks),
          ]}
        />
      ),
    },
    {
      key: 'inspection',
      label: '14. 지도·점검을 받은 사항',
      children: (
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="내용">
            <TextCell
              value={body.inspectionDetails}
              disabled={locked}
              maxLength={4000}
              onChange={(v) => set('inspectionDetails', v)}
            />
          </Descriptions.Item>
        </Descriptions>
      ),
    },
  ];

  return (
    <Card size="small" styles={{ body: { padding: 0 } }}>
      {/*
        1·2번 표(배출시설·방지시설 가동시간대)는 사업장에 등록된 시설로 채워진다.
        비어 있을 때만 알리면, 손으로 적기 시작한 사람은 원래 그런 줄 알고 계속
        적는다. 그래서 항상 둔다.
      */}
      <Alert
        type="info"
        showIcon
        style={{ margin: 12 }}
        message={
          <>
            폐수배출시설·수질오염방지시설은 <b>사업장·시설 메뉴 → 「사업장 시설관리」</b> 에서
            등록합니다.
          </>
        }
        description="거기에 등록해 두면 아래 1·2번 표가 자동으로 채워집니다. 등록하지 않으면 표가 빈 채로 열립니다."
        action={
          <Button size="small" onClick={() => navigate('/sites')}>
            사업장·시설로
          </Button>
        }
      />

      {/* 기본은 모두 펼침. 접혀 있으면 적어야 할 칸을 빠뜨린다. */}
      <Collapse defaultActiveKey={items.map((i) => i.key)} items={items} />
      <div style={{ padding: '8px 16px' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          ※ 「필수」 표시한 구역(인쇄 서식의 제1호~제4호)은 폐수처리방법 등 사업장 특성을 고려하여
          반드시 적어야 하며, 나머지(제5호~제12호)는 사업자의 판단에 따라 선택적으로 적을 수
          있습니다.
        </Text>
      </div>
    </Card>
  );
}
